import '../text';
import '../icon';
import { defineCustomElement, getBooleanAttribute, getAttribute, updateBooleanAttribute, updateAttribute, NectaryElement, getReactEventHandler, isAttrTrue, isAttrEqual } from '../utils';
const templateHTML = '<style>:host{display:inline-block;vertical-align:middle;outline:0;cursor:pointer}#button{all:initial;position:relative;display:flex;flex-direction:row;align-items:center;gap:4px;width:100%;height:var(--sinch-comp-chip-size-container-m);padding:0 5px 0 9px;border-radius:var(--sinch-comp-chip-shape-radius);background-color:var(--sinch-comp-chip-color-neutral-default-background-initial);box-sizing:border-box;--sinch-global-color-text:var(--sinch-comp-chip-color-neutral-default-foreground-initial);--sinch-global-color-icon:var(--sinch-comp-chip-color-neutral-default-foreground-initial);--sinch-global-size-icon:var(--sinch-comp-chip-size-icon-m)}:host(:focus-visible)>#button::after{content:"";position:absolute;inset:-4px;border-radius:calc(var(--sinch-comp-chip-shape-radius) + 4px);border:2px solid var(--sinch-comp-chip-color-outiline-focus);pointer-events:none}#text{flex:1;--sinch-comp-text-font:var(--sinch-comp-chip-font-size-m-label)}:host([small]) #button{height:var(--sinch-comp-chip-size-container-s);padding:0 3px 0 7px;--sinch-global-size-icon:var(--sinch-comp-chip-size-icon-s)}:host([small]) #text{--sinch-comp-text-font:var(--sinch-comp-chip-font-size-s-label)}::slotted([slot=icon]){margin-left:-4px}</style><div id="button" inert><slot name="icon"></slot><sinch-text id="text" type="xs" ellipsis></sinch-text><slot name="right-icon"><sinch-icon id="icon-close" name="cancel"></sinch-icon></slot></div>';
import { getChipColorBg, getChipColorFg } from './utils';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-chip', class extends NectaryElement {
  #$text;
  #$button;
  #controller = null;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$button = shadowRoot.querySelector('#button');
    this.#$text = shadowRoot.querySelector('#text');
  }
  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const {
      signal
    } = this.#controller;
    this.role = 'button';
    this.tabIndex = 0;
    this.addEventListener('click', this.#onClick, {
      signal
    });
    this.addEventListener('focus', this.#onFocus, {
      signal
    });
    this.addEventListener('blur', this.#onBlur, {
      signal
    });
    this.addEventListener('keydown', this.#onKeydown, {
      signal
    });
    this.addEventListener('-click', this.#onClickReactHandler, {
      signal
    });
    this.addEventListener('-focus', this.#onFocusReactHandler, {
      signal
    });
    this.addEventListener('-blur', this.#onBlurReactHandler, {
      signal
    });
    this.#updateColor();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#controller = null;
  }
  static get observedAttributes() {
    return ['text', 'color', 'small'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    if (isAttrEqual(oldVal, newVal)) {
      return;
    }
    switch (name) {
      case 'color':
        {
          this.#updateColor();
          break;
        }
      case 'text':
        {
          this.#$text.textContent = newVal;
          break;
        }
      case 'small':
        {
          updateBooleanAttribute(this, name, isAttrTrue(newVal));
          break;
        }
    }
  }
  get color() {
    return getAttribute(this, 'color');
  }
  set color(value) {
    updateAttribute(this, 'color', value);
  }
  get text() {
    return getAttribute(this, 'text', '');
  }
  set text(value) {
    updateAttribute(this, 'text', value);
  }
  get small() {
    return getBooleanAttribute(this, 'small');
  }
  set small(isSmall) {
    updateBooleanAttribute(this, 'small', isSmall);
  }
  #updateColor() {
    if (!this.isDomConnected) {
      return;
    }
    const colorName = this.color;
    if (colorName !== null && colorName.length > 0) {
      const bg = getChipColorBg(colorName);
      const fg = getChipColorFg(colorName);
      this.#$button.style.setProperty('background-color', bg);
      this.#$button.style.setProperty('--sinch-global-color-text', fg);
      this.#$button.style.setProperty('--sinch-global-color-icon', fg);
    } else {
      this.#$button.style.removeProperty('background-color');
      this.#$button.style.removeProperty('--sinch-global-color-text');
      this.#$button.style.removeProperty('--sinch-global-color-icon');
    }
  }
  get focusable() {
    return true;
  }
  #onKeydown = e => {
    switch (e.code) {
      case 'Space':
        {
          this.click();
        }
    }
  };
  #onClick = () => {
    this.dispatchEvent(new CustomEvent('-click'));
  };
  #onFocus = () => {
    this.dispatchEvent(new CustomEvent('-focus'));
  };
  #onBlur = () => {
    this.dispatchEvent(new CustomEvent('-blur'));
  };
  #onFocusReactHandler = () => {
    getReactEventHandler(this, 'on-focus')?.();
  };
  #onBlurReactHandler = () => {
    getReactEventHandler(this, 'on-blur')?.();
  };
  #onClickReactHandler = e => {
    getReactEventHandler(this, 'on-click')?.(e);
  };
});